import React from 'react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        
        // Pragmatic offline-safe error reporting
        if (process.env.NODE_ENV === 'production' && navigator.onLine) {
            // Send to backend error reporting only when online
            fetch('/api/errors/report', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: error.toString(),
                    stack: error.stack,
                    componentStack: errorInfo.componentStack,
                    userAgent: navigator.userAgent,
                    url: window.location.href,
                    timestamp: new Date().toISOString()
                })
            }).catch(reportError => {
                // Silent fail - don't crash the error boundary
                console.warn('Error reporting failed (user might be offline):', reportError);
            });
        } else if (process.env.NODE_ENV === 'production') {
            // Store error locally for later reporting when back online
            try {
                const storedErrors = JSON.parse(localStorage.getItem('gonkle_errors') || '[]');
                storedErrors.push({
                    message: error.toString(),
                    stack: error.stack,
                    componentStack: errorInfo.componentStack,
                    userAgent: navigator.userAgent,
                    url: window.location.href,
                    timestamp: new Date().toISOString()
                });
                
                // Keep only last 10 errors
                if (storedErrors.length > 10) {
                    storedErrors.splice(0, storedErrors.length - 10);
                }
                
                localStorage.setItem('gonkle_errors', JSON.stringify(storedErrors));
            } catch (storageError) {
                console.warn('Failed to store error locally:', storageError);
            }
        }
    }

    handleReset = () => {
        this.setState({ hasError: false });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: '20px',
                    backgroundColor: '#f8f9fa',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                    <div style={{
                        maxWidth: '400px',
                        textAlign: 'center',
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}>
                        <h1 style={{ 
                            color: '#dc3545', 
                            fontSize: '1.5rem', 
                            marginBottom: '1rem',
                            fontWeight: '600'
                        }}>
                            🚸 Oops! Something went wrong
                        </h1>
                        
                        <p style={{ 
                            color: '#6c757d', 
                            marginBottom: '1.5rem',
                            lineHeight: '1.6'
                        }}>
                            The app encountered an error. Please refresh the page.
                        </p>

                        <div style={{
                            display: 'flex',
                            gap: '1rem',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={this.handleReset}
                                style={{
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500'
                                }}
                            >
                                🔄 Try Again
                            </button>
                            
                            <button
                                onClick={() => window.location.reload()}
                                style={{
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500'
                                }}
                            >
                                🔄 Refresh Page
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
